import React, {FC, useEffect, useRef, useState} from "react";
import axios from "axios";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import moment from "moment";
import {useParams} from "react-router-dom";
import {CONSTANT, RANGE_BUTTON} from "../../../common/utils/constants";
import useCommon from "../../../common/hooks/useCommon";

interface RewardItem {
    rewardTime: string;
    reward: string;
}

interface ApiResponse {
    total: number;
    perPage: number;
    currentPage: number;
    pages: number;
    items: RewardItem[];
}

const RewardChart: FC = () => {
    const params = useParams();
    const {getXDC} = useCommon();
    const [loading, setLoading] = useState(true);
    const [options, setOptions] = useState({
        chart: {
            plotBackgroundColor: null,
            plotBorderWidth: null,
            plotShadow: false,
            type: "areaspline",
            zoomType: "",
            height: 500,
            spacingLeft: 0,
            spacingRight: 0,
        },
        title: {
            text: `${process.env.REACT_APP_BASE_UNIT || ""} Reward`,
        },
        exporting: {
            enabled: true,
        },
        credits: {enabled: false},
        subtitle: {
            x: 22,
            text: `Source: ${process.env.REACT_APP_BASE_EXPLORER || "Blocksscan"}.io`,
            align: "left",
        },
        marker: {
            radius: 2,
        },
        plotOptions: {
            series: {
                color: `#${CONSTANT.CHART_COLOUR}`,
                lineWidth: "2px",
                fillColor: {
                    linearGradient: {x1: 0, x2: 0, y1: 0, y2: 1},
                    stops: [
                        [0, `#${CONSTANT.CHART_COLOUR}66`],
                        [0.6, `#${CONSTANT.CHART_COLOUR}00`],
                    ],
                },
            },
        },
        xAxis: {
            type: "datetime",
            visible: true,
            title: {
                text: "Date",
            },
            labels: {
                formatter: function (this: any) {
                    return moment(this.value).format("DD MMM") + " <br/>";
                },
                padding: 0,
            },
            tickLength: 0,
        },
        yAxis: {
            opposite: false,
            title: {
                text: "Reward",
            },
            lineWidth: 0,
            gridLineWidth: 0,
            minorGridLineWidth: 0,
            lineColor: "transparent",
            labels: {
                autoRotation: false,
                padding: 0,
            },
            minorTickLength: 0,
            tickLength: 0,
        },
        legend: {
            enabled: false,
        },
        navigator: {
            enabled: false,
        },
        scrollbar: {
            enabled: false,
        },
        rangeSelector: {
            allButtonsEnabled: true,
            buttonPosition: {
                align: "left",
                x: -20,
                y: 0,
            },
            inputEnabled: true,
            buttons: RANGE_BUTTON,
        },
        series: [
            {
                name: "Reward",
                data: [],
                marker: {
                    enabled: false,
                },
            },
        ],
        tooltip: {
            enabled: true,
            useHTML: true,
            formatter: function (this: Highcharts.Point): string {
                const x = this.x ?? 0;
                const y = this.y ?? 0;
                const reward = parseFloat(y.toFixed(4));
                const date = moment(x).format("dddd, MMMM DD, YYYY");
                return `
                    <table>
                        <tr>
                            <td style="font-size: 10px;">${date}</td>
                        </tr>
                        <tr>
                            <td style="font-size: 12px;">[<span style="color: #${CONSTANT.CHART_COLOUR}" >Reward : </span> <span style="font-weight: bold; color: black;">${reward}</span>]</td>
                        </tr>
                    </table>`;
            },
        }
    });
    const chartRef = useRef<HighchartsReact.RefObject>(null);

    const [pagination, setPagination] = useState<{ page: number; limit: number }>({
        page: 1,
        limit: 50,
    });
    const [totalRecords, setTotalRecords] = useState(0);

    const fetchRewardData = async (startDate: string, endDate: string) => {
        setLoading(true);
        try {
            const response = await axios.get<ApiResponse>(
                `https://xdc.network/api/rewards/${getXDC(params.address)}?startDate=${startDate}&endDate=${endDate}&page=${pagination.page}&limit=${pagination.limit}`
            );

            const dataCharts = response.data.items.map(item => ({
                x: new Date(item.rewardTime).getTime(),
                y: parseFloat(item.reward),
            }));
            //@ts-ignore
            setOptions(prevOptions => ({
                ...prevOptions,
                series: [
                    {
                        name: "Reward",
                        data: dataCharts,
                    },
                ],
            }));
            setTotalRecords(response.data.total);
            setLoading(false)
        } catch (error) {
            console.error("Error fetching reward data", error);
            setLoading(false)
        } finally {
            setLoading(false);
        }
    };

    const handleRangeChange = (e: any) => {
        const {min, max} = e.target.chart.xAxis[0].getExtremes();
        const startDate = moment(min).toISOString();
        const endDate = moment(max).toISOString();
        fetchRewardData(startDate, endDate);
    };

    useEffect(() => {
        const endDate = moment().toISOString();
        const startDate = moment().subtract(1, 'month').toISOString();
        fetchRewardData(startDate, endDate);
    }, [params.address, pagination]);

    useEffect(() => {
        if (chartRef.current) {
            const chart = chartRef.current.chart;
            const logoUrl = `${process.env.REACT_APP_BASE_URL}/eaas/img/${process.env.REACT_APP_BASE_UNIT}-dark-logo.svg`;
            const logoWidth = 100;
            const logoHeight = 30;

            const existingLogo = chart.renderer.box.getElementsByClassName("chart-brand-logo");
            if (existingLogo.length) {
                Array.from(existingLogo).forEach((elem) => elem.remove());
            }

            chart.renderer
                .image(
                    logoUrl,
                    chart.plotLeft + (chart.plotWidth - logoWidth) / 2,
                    chart.plotTop + (chart.plotHeight - logoHeight) / 2,
                    logoWidth,
                    logoHeight
                )
                .addClass("chart-brand-logo")
                .add();
        }
    }, [options]);

    return (
        <div>
            {loading && <p>Loading...</p>}
            <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef}/>
        </div>
    );
};

export default RewardChart;
